import React, { useState, useEffect } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import api from '../../services/api';
import DepartmentsManager from './DepartmentsManager';


const UserManagement = ({ onRoleSelect, onViewPermissions }) => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    password: '',
    department_id: '',
    roles: []
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const [users, setUsers] = useState([]);
  const [adminTab, setAdminTab] = useState('users');
  const [showForm, setShowForm] = useState(false);
  const [metadata, setMetadata] = useState({ departments: [], roles: [] });
  const [customRole, setCustomRole] = useState('');
  const [customDept, setCustomDept] = useState('');

  // Search & Filter State
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');

  // New Modal States
  const [resetPwdUser, setResetPwdUser] = useState(null);
  const [deleteConfirmUser, setDeleteConfirmUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    fetchUsers();
    fetchMetadata();
  }, []);

  // Auto-dismiss notifications & optional redirect
  useEffect(() => {
    if (message.text) {
      const timer = setTimeout(() => {
        setMessage({ type: '', text: '' });
        // If success from form submission, go back to list
        if (message.type === 'success' && showForm) {
          setShowForm(false);
        }
      }, 2500);
      return () => clearTimeout(timer);
    }
  }, [message, showForm]);

  const fetchUsers = async () => {
    try {
      const resp = await api.get('/users');
      setUsers(resp.data);
    } catch (e) { setMessage({ type: 'error', text: 'Failed to fetch users' }); }
  };

  const fetchMetadata = async () => {
    try {
      const resp = await api.get('/metadata');
      setMetadata(resp.data);
    } catch (e) { 
      console.error(e);
      setMessage({ type: 'error', text: 'Backend services are offline. Please verify the API server is running.' });
    }
  };

  const handleToggleStatus = async (user) => {
    try {
      const currentStatus = Number(user.is_active);
      const nextStatus = currentStatus === 1 ? 0 : 1;
      
      await api.put(`/users/${user.user_id}/status`, { is_active: nextStatus });
      setMessage({ 
        type: 'success', 
        text: `Account for ${user.username} has been ${nextStatus === 0 ? 'suspended' : 'activated'} successfully.` 
      });
      fetchUsers();
    } catch (e) {
      if (e.response) {
        setMessage({ type: 'error', text: 'Status update failed on the server.' });
      } else {
        setMessage({ type: 'error', text: 'Network error: Could not update status.' });
      }
    }
  };

  const handleDelete = async (user) => {
    setDeleteConfirmUser(user);
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/users/${deleteConfirmUser.user_id}`);
      setDeleteConfirmUser(null);
      setMessage({ type: 'success', text: 'User entity permanently deleted from system.' });
      fetchUsers();
    } catch (e) { setMessage({ type: 'error', text: 'Deletion failed. Refer to logs.' }); }
  };

  const handleResetPassword = async (e) => {
    if (e) e.preventDefault();
    if (newPassword.length < 8) {
      setMessage({ type: 'error', text: 'Security bypass: Password must be at least 8 characters.' });
      return;
    }
    try {
      await api.post(`/users/${resetPwdUser.user_id}/reset-password`, { password: newPassword });
      setMessage({ type: 'success', text: `Credentials synchronized for ${resetPwdUser.username}.` });
      setResetPwdUser(null);
      setNewPassword('');
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Backend reset service failure.' });
    }
  };

  const handleRoleChange = (role) => {
    const newRoles = formData.roles.includes(role)
      ? formData.roles.filter(r => r !== role)
      : [...formData.roles, role];
    setFormData({ ...formData, roles: newRoles });
  };

  const validateName = (name) => name.length >= 2 && /^[a-zA-Z\s\-]+$/.test(name);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Strict Validation
    if (!validateName(formData.first_name) || !validateName(formData.last_name)) {
      setMessage({ type: 'error', text: 'Names must be at least 2 characters and letters only.' });
      return;
    }

    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^a-zA-Z0-9]).{8,}$/;
    if (!passwordRegex.test(formData.password)) {
      setMessage({ type: 'error', text: 'Password requirements: min 8 chars, uppercase, lowercase, number, special char.' });
      return;
    }

    const finalDept = customDept || formData.department_id;
    const finalRoles = [...formData.roles];
    if (customRole) finalRoles.push(customRole);

    if (!finalDept || finalRoles.length === 0) {
      setMessage({ type: 'error', text: 'Department and at least one role are required.' });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });
    
    try {
      await api.post('/users', { ...formData, department_id: finalDept, roles: finalRoles });
      setMessage({ type: 'success', text: 'User created successfully.' });
      setFormData({ username: '', email: '', first_name: '', last_name: '', password: '', department_id: '', roles: [] });
      setCustomDept('');
      setCustomRole('');
      setShowForm(false);
      fetchUsers();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Backend service unavailable.' });
    } finally {
      setLoading(false);
    }
  };

  // Permissions logic moved to UserPermissions.js

  // SavePermissions logic moved to UserPermissions.js

  // Live Filtering Logic
  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      (user.first_name + ' ' + user.last_name).toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.username.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesRole = roleFilter === '' || user.roles.includes(roleFilter);
    
    return matchesSearch && matchesRole;
  });

  if (adminTab === 'departments') {
    return (
      <div className="animate-fade-in" style={{ paddingBottom: '60px' }}>
        {/* Tab switcher */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', background: 'rgba(255,255,255,0.04)', borderRadius: '12px', padding: '4px', width: 'fit-content' }}>
          <button onClick={() => setAdminTab('users')} style={{ border: 'none', cursor: 'pointer', borderRadius: '10px', padding: '8px 24px', fontWeight: 700, fontSize: '13px', background: 'transparent', color: '#94a3b8', transition: 'all 0.2s' }}>Users</button>
          <button onClick={() => setAdminTab('departments')} style={{ border: 'none', cursor: 'pointer', borderRadius: '10px', padding: '8px 24px', fontWeight: 700, fontSize: '13px', background: '#3b82f6', color: 'white', boxShadow: '0 2px 8px rgba(59,130,246,0.4)', transition: 'all 0.2s' }}>Departments</button>
        </div>
        <DepartmentsManager />
      </div>
    );
  }

  return (
    <div className="animate-fade-in" style={{ paddingBottom: '60px' }}>
      {/* User Permission Logic moved to separate page */}

      {/* Reset Password Modal */}
      {resetPwdUser && (
        <div className="modal-overlay animate-fade-in">
          <div className="modal-card card glass animate-slide-up" style={{ maxWidth: '450px', width: '90%' }}>
            <h3 className="label-visible" style={{ fontSize: '20px', marginBottom: '16px' }}>Synchronize Credentials</h3>
            <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginBottom: '24px' }}>
              Setting a new mandatory password for <strong>{resetPwdUser.username}</strong>. Account status will remain {resetPwdUser.is_active ? 'Active' : 'Suspended'}.
            </p>
            <form onSubmit={handleResetPassword}>
              <div className="input-group">
                <label className="label-visible">New Master Password</label>
                <input 
                  type="password" 
                  value={newPassword} 
                  onChange={e => setNewPassword(e.target.value)} 
                  required 
                  placeholder="Min 8 chars, mixed case/symbols"
                  style={{ background: 'white', color: 'black' }}
                />
              </div>
              <div style={{ marginTop: '32px', display: 'flex', gap: '12px' }}>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>Apply Master Reset</button>
                <button type="button" className="btn btn-secondary" onClick={() => setResetPwdUser(null)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmUser && (
        <div className="modal-overlay animate-fade-in">
          <div className="modal-card card glass animate-slide-up" style={{ maxWidth: '450px', width: '90%', border: '1px solid #ef444444' }}>
            <h3 style={{ color: '#ef4444', fontSize: '20px', marginBottom: '16px' }}>Delete User Account?</h3>
            <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginBottom: '24px', lineHeight: '1.6' }}>
              This action will permanently remove <strong>{deleteConfirmUser.first_name} {deleteConfirmUser.last_name}</strong>. User will lose all access immediately.
            </p>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button className="btn" onClick={confirmDelete} style={{ flex: 1, background: '#ef4444', color: 'white' }}>Confirm Delete</button>
              <button className="btn btn-secondary" onClick={() => setDeleteConfirmUser(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Pop-up Notification Modal */}
      {message.text && (
        <div className="modal-overlay animate-fade-in" style={{ zIndex: 2000 }}>
          <div className="modal-card card glass animate-slide-up" style={{ 
            maxWidth: '400px', 
            width: '90%', 
            textAlign: 'center',
            border: `1px solid ${message.type === 'error' ? 'rgba(239, 68, 68, 0.3)' : 'rgba(34, 197, 94, 0.3)'}`
          }}>
            <div style={{ 
              fontSize: '48px', 
              marginBottom: '20px',
              color: message.type === 'error' ? '#ef4444' : '#22c55e'
            }}>
              {message.type === 'error' ? '⚠️' : '✅'}
            </div>
            <h3 className="label-visible" style={{ fontSize: '20px', marginBottom: '12px' }}>
              {message.type === 'error' ? 'System Alert' : 'Success'}
            </h3>
            <p style={{ color: 'var(--text-muted)', fontSize: '15px', marginBottom: '12px', lineHeight: '1.5' }}>
              {message.text}
            </p>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', opacity: 0.6 }}>
              Closing automatically...
            </div>
          </div>
        </div>
      )}

      {/* Tab switcher */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', background: 'rgba(255,255,255,0.04)', borderRadius: '12px', padding: '4px', width: 'fit-content' }}>
        <button onClick={() => setAdminTab('users')} style={{ border: 'none', cursor: 'pointer', borderRadius: '10px', padding: '8px 24px', fontWeight: 700, fontSize: '13px', background: '#3b82f6', color: 'white', boxShadow: '0 2px 8px rgba(59,130,246,0.4)', transition: 'all 0.2s' }}>Users</button>
        <button onClick={() => setAdminTab('departments')} style={{ border: 'none', cursor: 'pointer', borderRadius: '10px', padding: '8px 24px', fontWeight: 700, fontSize: '13px', background: 'transparent', color: '#94a3b8', transition: 'all 0.2s' }}>Departments</button>
      </div>

      <header style={{ marginBottom: '32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '20px' }}>
        <div>
          <h2 className="gradient-text" style={{ fontSize: '28px', marginBottom: '8px' }}>System Administration</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>Configure user accounts and role-based module visibility.</p>
        </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <div style={{ 
            background: '#000', 
            padding: '10px 20px', 
            borderRadius: '12px', 
            border: '1px solid rgba(255,255,255,0.1)', 
            display: 'flex', 
            alignItems: 'center', 
            gap: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
          }}>
            <label style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', fontWeight: 700, letterSpacing: '1px' }}>CONFIGURE ROLE:</label>
            <select 
              onChange={(e) => {
                const role = metadata.roles.find(r => r.id === e.target.value);
                if (role && typeof onRoleSelect === 'function') onRoleSelect(role);
              }}
              style={{ 
                background: 'transparent', 
                border: 'none', 
                color: '#fff', 
                fontWeight: 700, 
                cursor: 'pointer', 
                outline: 'none',
                fontSize: '14px'
              }}
              value=""
            >
              <option value="" disabled style={{ background: '#000' }}>Select Role...</option>
              {metadata.roles.map(r => (
                <option key={r.id} value={r.id} style={{ background: '#000' }}>
                  {r.name.replace('_', ' ').toUpperCase()}
                </option>
              ))}
            </select>
          </div>
          <button 
            className={`btn ${showForm ? 'btn-secondary' : 'btn-primary'}`}
            onClick={() => {
              setShowForm(!showForm);
              setMessage({ type: '', text: '' });
            }}
          >
            {showForm ? 'Back to User List' : 'Add New User Account'}
          </button>
        </div>
      </header>

      {showForm ? (
        <div className="form-container-centered">
          <div className="card glass animate-slide-up" style={{ padding: '40px' }}>
            <h3 className="label-visible" style={{ fontSize: '24px', marginBottom: '32px', textAlign: 'center' }}>Provision New Account</h3>
            <form onSubmit={handleSubmit} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
              <div className="input-group">
                <label className="label-visible">Username Identifier</label>
                <input type="text" value={formData.username} onChange={e => setFormData({...formData, username: e.target.value})} required placeholder="Unique username" />
              </div>
              <div className="input-group">
                <label className="label-visible">Institutional Email</label>
                <input type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} required placeholder="email@staff.msu.ac.zw" />
              </div>
              <div className="input-group">
                <label className="label-visible">First Name</label>
                <input type="text" value={formData.first_name} onChange={e => setFormData({...formData, first_name: e.target.value})} required placeholder="Legal first name" />
              </div>
              <div className="input-group">
                <label className="label-visible">Last Name</label>
                <input type="text" value={formData.last_name} onChange={e => setFormData({...formData, last_name: e.target.value})} required placeholder="Surname" />
              </div>
              <div className="input-group">
                <label className="label-visible">Initial Vault Password</label>
                <input type="password" value={formData.password} onChange={e => setFormData({...formData, password: e.target.value})} required placeholder="Complex mix required" />
              </div>
              <div className="input-group">
                <label className="label-visible">Managed Department</label>
                <select 
                  value={formData.department_id} 
                  onChange={e => {setFormData({...formData, department_id: e.target.value}); setCustomDept('')}}
                  style={{ 
                    width: '100%', 
                    padding: '12px', 
                    background: 'white', 
                    border: '1px solid #cbd5e1', 
                    borderRadius: '12px', 
                    color: '#1e293b',
                    fontSize: '14px',
                    fontWeight: 500
                  }}
                >
                  <option value="">Select Department</option>
                  {metadata.departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                  <option value="custom">+ New Department (Custom Entry)</option>
                </select>
                {formData.department_id === 'custom' && (
                  <div style={{ marginTop: '12px', padding: '16px', background: 'rgba(255,255,255,0.02)', borderRadius: '12px', border: '1px solid var(--primary)' }} className="animate-slide-up">
                    <label className="label-visible" style={{ color: 'var(--primary)' }}>Enter New Department Name</label>
                    <input 
                      type="text" 
                      placeholder="e.g. Strategic Planning" 
                      value={customDept}
                      onChange={e => setCustomDept(e.target.value)}
                      required 
                      style={{ 
                        background: 'white', 
                        color: '#1e293b', 
                        border: '1px solid #cbd5e1',
                        fontWeight: 500
                      }}
                    />
                  </div>
                )}
              </div>
              
              <div className="input-group" style={{ gridColumn: 'span 2' }}>
                <label className="label-visible" style={{ fontSize: '16px', marginBottom: '20px' }}>System Access Roles</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                  {metadata.roles.map(role => (
                    <label key={role.id} style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '12px', 
                      cursor: 'pointer', 
                      padding: '16px', 
                      background: formData.roles.includes(role.name) ? 'rgba(34, 197, 94, 0.05)' : 'rgba(255,255,255,0.02)', 
                      borderRadius: '12px', 
                      border: `1px solid ${formData.roles.includes(role.name) ? 'var(--primary)' : 'var(--border-color)'}`,
                      transition: 'all 0.2s'
                    }}>
                      <input 
                        type="checkbox" 
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                        checked={formData.roles.includes(role.name)} 
                        onChange={() => handleRoleChange(role.name)} 
                      />
                      <span style={{ fontSize: '14px', fontWeight: 500, color: formData.roles.includes(role.name) ? 'white' : 'var(--text-secondary)' }}>{role.name.replace('_', ' ')}</span>
                    </label>
                  ))}
                </div>
                
                <div style={{ marginTop: '24px', padding: '20px', background: 'rgba(255,255,255,0.03)', borderRadius: '12px', border: '1px dashed var(--border-color)' }}>
                  <label className="label-visible" style={{ color: 'var(--text-muted)' }}>Propose Custom Role</label>
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <input 
                      type="text" 
                      placeholder="e.g. SHE Inspector (Field)" 
                      style={{ background: 'white', color: 'black', borderRadius: '8px' }} 
                      value={customRole} 
                      onChange={e => setCustomRole(e.target.value)} 
                    />
                  </div>
                </div>
              </div>

              <div style={{ gridColumn: 'span 2', display: 'flex', gap: '16px', marginTop: '12px' }}>
                <button type="submit" className="btn btn-primary" style={{ flex: 1, height: '54px' }}>
                  {loading ? 'Creating Account...' : 'Add New User Account'}
                </button>
                <button type="button" className="btn btn-secondary" style={{ width: '120px' }} onClick={() => setShowForm(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      ) : (
        <>
          {/* Search & Filter Bar */}
          <div className="card glass" style={{ padding: '20px', marginBottom: '24px', display: 'flex', gap: '16px', alignItems: 'center' }}>
            <div style={{ flex: 1, position: 'relative' }}>
              <input 
                type="text" 
                placeholder="Search by name, email, or username..." 
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                style={{ 
                  paddingLeft: '44px', 
                  background: 'white', 
                  color: '#1e293b', 
                  border: '1px solid #cbd5e1',
                  borderRadius: '12px',
                  fontWeight: 500
                }}
              />
              <span style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', opacity: 0.6, color: '#64748b', display: 'flex' }}>
                <SearchIcon style={{ fontSize: '20px' }} />
              </span>
            </div>
            <select 
              value={roleFilter} 
              onChange={e => setRoleFilter(e.target.value)}
              style={{ 
                width: '200px', 
                background: 'white', 
                color: '#1e293b', 
                border: '1px solid #cbd5e1',
                borderRadius: '12px',
                fontWeight: 500
              }}
            >
              <option value="">All Roles</option>
              {metadata.roles.map(r => <option key={r.id} value={r.name}>{r.name.replace('_', ' ')}</option>)}
            </select>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontWeight: 600 }}>
              {filteredUsers.length} Users Found
            </div>
          </div>

          <div className="card glass animate-fade-in" style={{ padding: 0, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'rgba(255,255,255,0.05)', textAlign: 'left' }}>
                  <th style={{ padding: '20px' }}>User Name</th>
                  <th>Department</th>
                  <th>Roles</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'right', paddingRight: '20px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map(user => (
                  <tr key={user.user_id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                    <td style={{ padding: '20px' }}>
                      <div style={{ fontWeight: 600 }}>{user.first_name} {user.last_name}</div>
                      <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{user.email}</div>
                    </td>
                    <td>{user.department_name}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                        {user.roles.map(r => (
                          <span key={r} style={{ fontSize: '10px', background: 'var(--primary)', padding: '2px 8px', borderRadius: '10px', textTransform: 'capitalize' }}>{r.replace('_', ' ')}</span>
                        ))}
                      </div>
                    </td>
                    <td>
                      <span className={`badge ${Number(user.is_active) === 1 ? 'badge-success' : 'badge-error'}`}>
                        {Number(user.is_active) === 1 ? 'Active' : 'Suspended'}
                      </span>
                    </td>
                    <td style={{ textAlign: 'right', paddingRight: '20px' }}>
                      <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                        <button className="btn-badge" onClick={() => onViewPermissions(user)}>Permissions</button>
                        <button className="btn-badge" onClick={() => setResetPwdUser(user)}>Reset</button>
                        <button 
                          className={`btn-badge ${Number(user.is_active) === 1 ? 'btn-badge-danger' : 'badge-success'}`} 
                          onClick={() => handleToggleStatus(user)}
                        >
                          {Number(user.is_active) === 1 ? 'Suspend' : 'Activate'}
                        </button>
                        <button className="btn-badge btn-badge-danger" onClick={() => handleDelete(user)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default UserManagement;
